import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Package,
  RefreshCw,
  Save,
  ShoppingBag,
  Unplug,
  Warehouse,
} from 'lucide-react';
import { authApi, ebayApi } from '@/services/api';
import { EBAY_OAUTH_SCOPES } from '@/components/settings/setupGuides';

/**
 * eBay Marketplaces configure panel — OAuth + policies + sync actions.
 * Includes “How to get credentials from eBay” (Developer portal / Seller Hub), not SaleDesk enable steps.
 */
export default function EbayIntegrationTab() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showToken, setShowToken] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [copied, setCopied] = useState<'scopes' | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const packageAllows =
    Boolean(profile?.allow_ebay) ||
    features.includes('allow_ebay') ||
    (Array.isArray(profile?.selected_add_ons) &&
      profile.selected_add_ons.some(
        (a: string | { status?: string; feature_key?: string; addon_code?: string }) => {
          if (typeof a === 'string') return a === 'allow_ebay';
          if (a?.status && a.status !== 'Active') return false;
          return a?.feature_key === 'allow_ebay' || a?.addon_code === 'allow_ebay';
        },
      ));

  const { data: status, refetch } = useQuery({
    queryKey: ['ebayStatus'],
    queryFn: async () => (await ebayApi.getStatus()).data.message,
    enabled: packageAllows,
  });

  const [form, setForm] = useState({
    enable_ebay: false,
    ebay_marketplace_id: 'EBAY_GB',
    ebay_merchant_location_key: '',
    ebay_default_category_id: '',
    ebay_fulfillment_policy_id: '',
    ebay_payment_policy_id: '',
    ebay_return_policy_id: '',
    ebay_sync_products_mode: 'Selected',
    ebay_auto_confirm_orders: false,
    ebay_auto_fetch_orders: false,
    ebay_access_token: '',
    ebay_refresh_token: '',
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => ({
      ...f,
      enable_ebay: Boolean(profile.enable_ebay),
      ebay_marketplace_id: profile.ebay_marketplace_id || 'EBAY_GB',
      ebay_merchant_location_key: profile.ebay_merchant_location_key || '',
      ebay_default_category_id: profile.ebay_default_category_id || '',
      ebay_fulfillment_policy_id: profile.ebay_fulfillment_policy_id || '',
      ebay_payment_policy_id: profile.ebay_payment_policy_id || '',
      ebay_return_policy_id: profile.ebay_return_policy_id || '',
      ebay_sync_products_mode: profile.ebay_sync_products_mode || 'Selected',
      ebay_auto_confirm_orders: Boolean(profile.ebay_auto_confirm_orders),
      ebay_auto_fetch_orders: Boolean(profile.ebay_auto_fetch_orders),
    }));
  }, [profile]);

  useEffect(() => {
    const flag = searchParams.get('ebay');
    if (!flag) return;
    if (flag === 'connected') {
      toast.success('eBay connected');
      void refetch();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } else if (flag === 'error') {
      toast.error(`eBay connect failed: ${searchParams.get('reason') || ''}`);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('ebay');
    next.delete('reason');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (!form.ebay_access_token) delete payload.ebay_access_token;
      if (!form.ebay_refresh_token) delete payload.ebay_refresh_token;
      await authApi.updateCompanyProfile(payload);
    },
    onSuccess: () => {
      toast.success('eBay settings saved');
      qc.invalidateQueries({ queryKey: ['profile'] });
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Save failed'),
  });

  const run = async (fn: () => Promise<any>, okText: string) => {
    setMsg(null);
    try {
      const res = await fn();
      setMsg({ ok: true, text: okText + (res?.message ? `: ${res.message}` : '') });
      void refetch();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e.message || 'Failed' });
    }
  };

  const scopesCsv = EBAY_OAUTH_SCOPES.join(' ');
  const copyScopes = async () => {
    try {
      await navigator.clipboard.writeText(scopesCsv);
      setCopied('scopes');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!packageAllows) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-3 max-w-xl">
        <p className="font-medium">eBay add-on required</p>
        <p>Contact Support to purchase the eBay integration for your company.</p>
        <Link to="/support?subject=Enable%20eBay%20integration" className="btn-primary text-sm w-fit inline-flex">
          Contact Support
        </Link>
      </div>
    );
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="space-y-5 max-w-3xl min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">eBay</h2>
            <p className="text-sm text-gray-500">UK marketplace (EBAY_GB) — listings, stock and orders</p>
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {connected ? 'Connected' : status?.connection_status || 'Disconnected'}
        </span>
      </div>

      {msg && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${
            msg.ok ? 'border-emerald-100 bg-emerald-50 text-emerald-900' : 'border-red-100 bg-red-50 text-red-800'
          }`}
        >
          {msg.ok ? <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* How to get credentials from eBay (Developer portal + Seller Hub) */}
      <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/80 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-blue-900 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">How to get credentials from eBay</span>
          </span>
          {guideOpen ? (
            <ChevronUp className="w-4 h-4 text-blue-700 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-blue-700 shrink-0" />
          )}
        </button>

        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">
              Collect tokens, policy IDs, location key and category from eBay — then paste them into the form
              below. Prefer <strong>Connect with eBay</strong> when it is enabled; otherwise use User Tokens from
              the Developer portal.
            </p>

            <div className="rounded-lg border border-blue-200 bg-white px-3 py-2.5 text-xs text-blue-900 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                <a
                  href="https://developer.ebay.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-blue-600/40 font-bold"
                >
                  developer.ebay.com
                </a>
                {' '}→ My Account →{' '}
                <span className="font-bold underline decoration-blue-600/40">Application Keysets</span>
                {' '}→ Create a keyset (Production)
              </p>
              <p className="mt-1.5 text-gray-600 break-words">
                Direct keys page:{' '}
                <a
                  href="https://developer.ebay.com/my/keys"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 underline break-all"
                >
                  developer.ebay.com/my/keys
                </a>
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900">On the eBay Developer / Seller forms</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">Open Application Keysets</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Sign in with your seller eBay account. Join the Developers Program if asked. Open{' '}
                  <strong>My Account → Application Keysets</strong>.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Create a Production keyset</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Click <strong>Create a keyset</strong> and choose <strong>Production</strong> (live UK selling).
                  Give the app a clear title, e.g. SaleDesk POS.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Application title:</span>{' '}
                    <code className="font-semibold text-gray-900">SaleDesk POS</code>
                  </p>
                  <p>
                    <span className="text-gray-500">Environment:</span>{' '}
                    <span className="font-medium">Production</span> (not Sandbox)
                  </p>
                </div>
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Sandbox does not return real orders. Use a Production keyset for SaleDesk live sync on EBAY_GB.
                  </span>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Copy App ID, Cert ID &amp; Dev ID</span>
                <p className="mt-1 ml-5 text-gray-600">
                  After the keyset is created, open it and copy these values (needed for OAuth / Support app
                  setup):
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">App ID (Client ID):</span> copy from keyset
                  </p>
                  <p>
                    <span className="text-gray-500">Cert ID (Client Secret):</span> reveal &amp; copy once
                  </p>
                  <p>
                    <span className="text-gray-500">Dev ID:</span> copy from keyset
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">User Tokens — add Redirect URL / RuName</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Next to your App ID, open <strong>User Tokens</strong>. Under “Get a Token from eBay via Your
                  Application”, click <strong>Add eBay Redirect URL</strong>, turn on <strong>OAuth Enabled</strong>,
                  save, then copy the <strong>RuName</strong> (Redirect URL Name). RuName is an identifier — not a
                  normal https URL.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Display title:</span>{' '}
                    <code className="font-semibold text-gray-900">SaleDesk POS</code>
                  </p>
                  <p>
                    <span className="text-gray-500">OAuth Enabled:</span>{' '}
                    <span className="font-medium">checked</span>
                  </p>
                  <p>
                    <span className="text-gray-500">RuName:</span> copy after Save (e.g.{' '}
                    <code>YourApp-SaleDesk-PRD-xxxxx</code>)
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">OAuth scopes (API access)</span>
                <p className="mt-1 ml-5 text-gray-600">
                  When eBay asks which APIs / scopes to allow for your app, include at least Inventory,
                  Fulfillment and Account (sell) scopes:
                </p>
                <div className="mt-2 ml-5 flex flex-wrap gap-1.5">
                  {EBAY_OAUTH_SCOPES.map((s) => (
                    <code
                      key={s}
                      className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-blue-100 text-blue-900 break-all"
                    >
                      {s.replace('https://api.ebay.com/oauth/api_scope/', '') || 'api_scope'}
                    </code>
                  ))}
                </div>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 min-w-0">
                  <code className="min-w-0 flex-1 text-[11px] bg-white border border-blue-100 rounded px-2 py-1.5 break-all whitespace-pre-wrap">
                    {scopesCsv}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyScopes()}
                    className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center justify-center gap-1 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy scopes
                  </button>
                </div>
                {copied === 'scopes' && (
                  <p className="ml-5 text-xs text-emerald-600 mt-1">Copied</p>
                )}
              </li>

              <li>
                <span className="font-medium text-gray-900">Get Access token &amp; Refresh token</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Still under <strong>User Tokens</strong>, use “Sign in to Production” / OAuth consent with your
                  seller account, then copy the <strong>Access token</strong> and <strong>Refresh token</strong>.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">Paste into SaleDesk (below)</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>
                      Access token → <strong>Access token</strong> field
                    </li>
                    <li>
                      Refresh token → <strong>Refresh token</strong> field
                    </li>
                    <li>
                      Or skip pasting and use <strong>Connect with eBay</strong> when that button is enabled
                    </li>
                  </ol>
                  <p className="text-gray-600 border-t border-emerald-200/80 pt-2">
                    Never share these tokens in chat or email.
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Business policy IDs (Seller Hub)</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open eBay Seller Hub → <strong>Account</strong> (or Settings) →{' '}
                  <strong>Business policies</strong>. Create Payment, Return and Fulfilment/Shipping policies if
                  needed. Click a policy name — the <strong>Profile ID</strong> is the number at the end of the
                  browser URL after <code>=</code>.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Fulfilment / Shipping Profile ID →</span> Fulfillment policy ID
                  </p>
                  <p>
                    <span className="text-gray-500">Payment Profile ID →</span> Payment policy ID
                  </p>
                  <p>
                    <span className="text-gray-500">Return Profile ID →</span> Return policy ID
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Merchant location key</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Use your eBay inventory location key (warehouse). Create a location in Seller Hub / Inventory if
                  you do not have one. After tokens are saved, click <strong>Test connection</strong> in SaleDesk —
                  returned locations often include the key to paste.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Default category ID</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Use the numeric leaf category for your products (from an existing UK listing, Seller Hub category
                  picker, or eBay Taxonomy / Category Tree on developer.ebay.com). Paste into{' '}
                  <strong>Default category ID</strong>.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Marketplace ID:</span>{' '}
                    <code className="font-semibold text-gray-900">EBAY_GB</code> (UK)
                  </p>
                </div>
              </li>
            </ol>

            <div className="rounded-lg bg-white/80 border border-blue-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              <p>• Keep SKUs identical between SaleDesk and eBay listings.</p>
              <p>• Start with Selected (Show on eBay) sync before All active.</p>
              <p>
                • Official docs:{' '}
                <a
                  href="https://developer.ebay.com/api-docs/static/oauth-tokens.html"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 underline"
                >
                  OAuth tokens
                </a>
                {' · '}
                <a
                  href="https://developer.ebay.com/my/keys"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 underline"
                >
                  Application keys
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.enable_ebay}
            onChange={(e) => setForm({ ...form, enable_ebay: e.target.checked })}
          />
          Enable eBay integration
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Marketplace ID</label>
            <input
              className="input-field mt-1"
              value={form.ebay_marketplace_id}
              onChange={(e) => setForm({ ...form, ebay_marketplace_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Merchant location key</label>
            <input
              className="input-field mt-1"
              value={form.ebay_merchant_location_key}
              onChange={(e) => setForm({ ...form, ebay_merchant_location_key: e.target.value })}
              placeholder="From Test connection → locations"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Default category ID</label>
            <input
              className="input-field mt-1"
              value={form.ebay_default_category_id}
              onChange={(e) => setForm({ ...form, ebay_default_category_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Product sync mode</label>
            <select
              className="input-field mt-1"
              value={form.ebay_sync_products_mode}
              onChange={(e) => setForm({ ...form, ebay_sync_products_mode: e.target.value })}
            >
              <option value="Selected">Selected (Show on eBay)</option>
              <option value="All active">All active</option>
              <option value="Manual">Manual only</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Fulfillment policy ID</label>
            <input
              className="input-field mt-1"
              value={form.ebay_fulfillment_policy_id}
              onChange={(e) => setForm({ ...form, ebay_fulfillment_policy_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Payment policy ID</label>
            <input
              className="input-field mt-1"
              value={form.ebay_payment_policy_id}
              onChange={(e) => setForm({ ...form, ebay_payment_policy_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Return policy ID</label>
            <input
              className="input-field mt-1"
              value={form.ebay_return_policy_id}
              onChange={(e) => setForm({ ...form, ebay_return_policy_id: e.target.value })}
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.ebay_auto_fetch_orders}
              onChange={(e) => setForm({ ...form, ebay_auto_fetch_orders: e.target.checked })}
            />
            Auto-fetch orders (every 5 min)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.ebay_auto_confirm_orders}
              onChange={(e) => setForm({ ...form, ebay_auto_confirm_orders: e.target.checked })}
            />
            Auto-confirm mapped orders
          </label>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">Connect</p>
          <p className="text-xs text-gray-500">
            Prefer one-click OAuth when Support has configured the SaleDesk eBay app. You can also paste tokens
            from the eBay Developer portal (never share them in chat).
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              disabled={!status?.oauth_app_configured}
              onClick={() =>
                void run(async () => {
                  const r = await ebayApi.getOauthUrl();
                  const url = r.data.message?.url;
                  if (url) window.location.href = url;
                  return { message: 'Redirecting…' };
                }, 'Opening eBay')
              }
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Connect with eBay
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => void run(() => ebayApi.testConnection().then((r) => r.data.message), 'Connection OK')}
            >
              Test connection
            </button>
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-1.5 text-red-700"
              onClick={() =>
                void run(async () => {
                  await ebayApi.disconnect();
                  qc.invalidateQueries({ queryKey: ['profile'] });
                  return {};
                }, 'Disconnected')
              }
            >
              <Unplug className="w-3.5 h-3.5" />
              Disconnect
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">
                Access token {profile?.has_ebay_token ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showToken ? 'text' : 'password'}
                  value={form.ebay_access_token}
                  placeholder={profile?.has_ebay_token ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, ebay_access_token: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowToken((v) => !v)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                Refresh token {profile?.has_ebay_refresh_token ? '(saved)' : ''}
              </label>
              <input
                className="input-field mt-1"
                type="password"
                value={form.ebay_refresh_token}
                placeholder={profile?.has_ebay_refresh_token ? '••••••••' : ''}
                onChange={(e) => setForm({ ...form, ebay_refresh_token: e.target.value })}
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            onClick={() => void run(() => ebayApi.syncProducts().then((r) => r.data.message), 'Product sync queued')}
          >
            <Package className="w-3.5 h-3.5" /> Sync products
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            onClick={() => void run(() => ebayApi.syncInventory().then((r) => r.data.message), 'Inventory synced')}
          >
            <Warehouse className="w-3.5 h-3.5" /> Sync stock
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            onClick={() => void run(() => ebayApi.fetchOrders().then((r) => r.data.message), 'Orders fetched')}
          >
            <Download className="w-3.5 h-3.5" /> Fetch orders
          </button>
          <button type="button" className="btn-secondary text-sm inline-flex items-center gap-1.5" onClick={() => void refetch()}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh status
          </button>
          <Link to="/channel-orders?channel=ebay" className="btn-secondary text-sm w-fit">
            Marketplace Orders
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Tick <strong>Show on eBay</strong> on products to include them in Selected sync. Set location + category +
          policy IDs (via Test connection) before publishing live offers.
        </p>
      </div>
    </div>
  );
}
